import {
  evolusiteShareCents,
  formatEuros,
  subcontractCostCents,
  subcontractedCents,
  type PutQuoteSubcontractingBody,
  type QuoteSubcontracting,
  type SubcontractMode,
  type Supplier,
  type SupplierList,
} from '@cockpit/shared';
import { matchesOption, type SearchOption } from './searchSelect';

/**
 * Lot AY « Sous-traitance » : logique pure de l'écran (bloc de saisie, fiche, reprise), testée sans navigateur.
 */

export interface LineForm {
  checked: boolean;
  supplierId: string | null;
  /** Coût payé, en centimes (null : champ vide ou illisible). */
  costCents: number | null;
}

export interface SubcontractForm {
  mode: SubcontractMode;
  lines: Record<string, LineForm>;
  global: { supplierId: string | null; costCents: number | null };
}

/** Formulaire de départ : la saisie enregistrée, sinon « Par ligne » sans ligne cochée. */
export function formFromDto(dto: QuoteSubcontracting): SubcontractForm {
  const lines: Record<string, LineForm> = {};
  for (const line of dto.lines) {
    lines[line.key] = line.subcontract
      ? { checked: true, supplierId: line.subcontract.supplierId, costCents: line.subcontract.costCents }
      : { checked: false, supplierId: null, costCents: line.amountCents };
  }
  return {
    mode: dto.status === 'unset' ? 'lines' : dto.status,
    lines,
    global: dto.global
      ? { supplierId: dto.global.supplierId, costCents: dto.global.costCents }
      : { supplierId: null, costCents: null },
  };
}

/** Coche une ligne : le coût est prérempli avec le prix de la ligne, le sous-traitant avec le dernier choisi. */
export function checkLine(
  form: SubcontractForm,
  key: string,
  checked: boolean,
  lineAmountCents: number,
): SubcontractForm {
  const current = form.lines[key] ?? { checked: false, supplierId: null, costCents: lineAmountCents };
  const lastSupplier =
    Object.values(form.lines).find((l) => l.checked && l.supplierId)?.supplierId ?? form.global.supplierId;
  return {
    ...form,
    lines: {
      ...form.lines,
      [key]: checked
        ? {
            checked: true,
            supplierId: current.supplierId ?? lastSupplier ?? null,
            costCents: current.costCents ?? lineAmountCents,
          }
        : { ...current, checked: false },
    },
  };
}

/** Coût et part d'Evolusite en direct, avec ce qui manque pour enregistrer. */
export function formTotals(
  dto: Pick<QuoteSubcontracting, 'amountCents' | 'lines'>,
  form: SubcontractForm,
): { costCents: number; subcontractedCents: number; evolusiteCents: number; missing: string | null } {
  let missing: string | null = null;
  const amounts =
    form.mode === 'lines'
      ? {
          lineCosts: dto.lines.flatMap((line) => {
            const f = form.lines[line.key];
            if (!f?.checked) return [];
            if (!f.supplierId) missing ??= `Choisissez le sous-traitant de « ${line.label} ».`;
            if (f.costCents === null) missing ??= `Coût illisible pour « ${line.label} ».`;
            return [{ index: line.index, costCents: f.costCents ?? 0 }];
          }),
          proratedCents: 0,
        }
      : form.mode === 'global'
        ? { lineCosts: [], proratedCents: form.global.costCents ?? 0 }
        : { lineCosts: [], proratedCents: 0 };
  if (form.mode === 'lines' && amounts.lineCosts.length === 0)
    missing ??= 'Cochez au moins une ligne sous-traitée, ou choisissez « Aucune ».';
  if (form.mode === 'global') {
    if (!form.global.supplierId) missing ??= 'Choisissez le sous-traitant.';
    if (!form.global.costCents || form.global.costCents <= 0) missing ??= 'Indiquez le montant sous-traité.';
  }
  return {
    costCents: subcontractCostCents(amounts),
    subcontractedCents: subcontractedCents(dto.amountCents, amounts),
    evolusiteCents: evolusiteShareCents(dto.amountCents, amounts),
    missing,
  };
}

/** Corps de `PUT /api/quotes/:id/subcontracting`. */
export function bodyFromForm(
  dto: Pick<QuoteSubcontracting, 'lines'>,
  form: SubcontractForm,
): PutQuoteSubcontractingBody {
  if (form.mode === 'none') return { mode: 'none', lines: [], global: null };
  if (form.mode === 'global') {
    return {
      mode: 'global',
      lines: [],
      global: { supplierId: form.global.supplierId ?? '', costCents: form.global.costCents ?? 0 },
    };
  }
  return {
    mode: 'lines',
    lines: dto.lines.flatMap((line) => {
      const f = form.lines[line.key];
      return f?.checked
        ? [{ key: line.key, supplierId: f.supplierId ?? '', costCents: f.costCents ?? 0 }]
        : [];
    }),
    global: null,
  };
}

/** Sous-traitants d'abord (« Sous-traitant »), puis les autres fournisseurs Axonaut. */
export function supplierOptions(suppliers: readonly Supplier[]): SearchOption[] {
  return [...suppliers]
    .sort(
      (a, b) => Number(b.isSubcontractor) - Number(a.isSubcontractor) || a.name.localeCompare(b.name, 'fr'),
    )
    .map((s) => ({
      value: s.id,
      label: s.name,
      secondary: [s.isSubcontractor ? 'Sous-traitant' : 'Fournisseur Axonaut', ...s.categories].join(' · '),
    }));
}

/** Les sous-traitants d'un devis (« Studio Nord, Atelier Brume »). */
export function suppliersOf(dto: QuoteSubcontracting): string {
  return [
    ...new Set([
      ...dto.lines.flatMap((l) => (l.subcontract ? [l.subcontract.supplierName] : [])),
      ...(dto.global ? [dto.global.supplierName] : []),
      ...dto.orphans.map((o) => o.supplierName),
    ]),
  ].join(', ');
}

/** « 15/02/2027 » */
export function frDate(date: string): string {
  return `${date.slice(8, 10)}/${date.slice(5, 7)}/${date.slice(0, 4)}`;
}

// ------------------------------------------------------------------ Réglages > Chiffres : fournisseurs

/** Fournisseurs affichés d'un coup dans Réglages (« Afficher plus » ajoute la suite). */
export const SUPPLIERS_PAGE = 60;

/** Tous les fournisseurs (sous-traitants d'abord, puis les autres par nom), filtrés par la recherche. */
export function filterSuppliers(suppliers: readonly Supplier[], query: string): Supplier[] {
  const q = query.trim();
  return [...suppliers]
    .filter((s) => !q || matchesOption({ value: s.id, label: s.name, secondary: s.categories.join(' ') }, q))
    .sort(
      (a, b) => Number(b.isSubcontractor) - Number(a.isSubcontractor) || a.name.localeCompare(b.name, 'fr'),
    );
}

/** « 20/10/2026 à 14:05 » (heure de Paris). */
export function frDateTime(iso: string): string {
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')}/${get('month')}/${get('year')} à ${get('hour')}:${get('minute')}`;
}

/** Messages de la dernière lecture des fournisseurs (échec, illisibles ignorés, jamais lus). */
export function supplierReadMessages(list: Pick<SupplierList, 'items' | 'syncedAt' | 'lastRead'>): {
  error: string | null;
  skipped: string | null;
  neverRead: boolean;
} {
  const read = list.lastRead;
  return {
    error: read?.error
      ? `La dernière lecture des fournisseurs Axonaut a échoué (${frDateTime(read.at)}) : ${read.error}`
      : null,
    skipped:
      read && read.skipped > 0
        ? read.skipped === 1
          ? '1 fournisseur illisible ignoré (les autres sont lus).'
          : `${read.skipped} fournisseurs illisibles ignorés (les autres sont lus).`
        : null,
    neverRead: !read && !list.syncedAt && list.items.length === 0,
  };
}

/** « début du projet le 15/11/2026 + 3 mois (réglage de Chiffres) » ou « date choisie à la main ». */
export function dueExplanation(due: QuoteSubcontracting['due']): string {
  if (due.overridden) return 'date choisie à la main pour ce devis';
  if (!due.start) return 'date de départ inconnue';
  const start =
    due.startSource === 'project'
      ? `début du projet le ${frDate(due.start)}`
      : `signature le ${frDate(due.start)} (pas encore de projet)`;
  return `${start} + ${due.delayMonths} mois (réglage de Chiffres)`;
}

/** « 3 100 € signés, dont 1 900 € sous-traités » (rien sans sous-traitance). */
export function grossText(grossCents: number, subcontractedCentsValue: number): string | null {
  if (subcontractedCentsValue <= 0) return null;
  return `${formatEuros(grossCents)} signés, dont ${formatEuros(subcontractedCentsValue)} sous-traités`;
}

/** Libellé court de l'état d'un devis dans la reprise et la page client. */
export function statusPill(item: {
  status: 'unset' | 'lines' | 'global' | 'none';
  subcontractedCents: number;
  suppliers?: readonly string[];
  hint?: string | null;
}): { text: string; tone: 'todo' | 'ok' | '' } {
  if (item.status === 'unset')
    return item.hint ? { text: 'À vérifier', tone: 'todo' } : { text: 'À voir', tone: '' };
  if (item.status === 'none' || item.subcontractedCents <= 0) return { text: 'Aucune', tone: 'ok' };
  const who = item.suppliers && item.suppliers.length > 0 ? ` · ${item.suppliers.join(', ')}` : '';
  return { text: `${formatEuros(item.subcontractedCents)}${who}`, tone: 'ok' };
}
